import type { BlockDto } from '@klickibunti/pagebuilder-react';
import type { AnyFrontendWidget } from '@/src/widgets/registry';

export interface InlineResolved {
  id: BlockDto['id'];
  resolved: unknown;
}

export interface LookupRequest {
  /** Composite key — see {@link lookupKeyFor} for the rationale. */
  id: string;
  componentType: string;
  config: Record<string, unknown>;
}

export interface OrchestrationResult {
  inline: InlineResolved[];
  lookups: LookupRequest[];
}

// Strapi assigns dynamic-zone component ids per component-type table, so two
// blocks of different types can share id=1 (e.g. an offer-widget and a
// snippet-reference). Using `block.id` alone as the resolve-widgets request id
// causes one to overwrite the other in the response. Prefix with the component
// type to make the key unique across the page.
export function lookupKeyFor(block: {
  componentType: string;
  id: BlockDto['id'];
}): string {
  return `${block.componentType}#${block.id}`;
}

// Recurse into nested dynamic zones for layout widgets so their children
// also feed into the orchestrator. We walk every BlockDto field that's an
// array of BlockDto.
function nestedZonesOf(block: BlockDto): BlockDto[][] {
  const zones: BlockDto[][] = [];
  for (const value of Object.values(block)) {
    if (Array.isArray(value) && value.length > 0) {
      const first = value[0] as Partial<BlockDto>;
      if (
        first &&
        typeof first === 'object' &&
        'componentType' in first &&
        'id' in first
      ) {
        zones.push(value as BlockDto[]);
      }
    }
  }
  return zones;
}

function stripBlockHousekeeping(block: BlockDto): Record<string, unknown> {
  const { id: _id, componentType: _ct, ...rest } = block;
  void _id;
  void _ct;
  return rest;
}

// Walks the block tree depth-first. For each block, looks up its FE
// registry entry. If the entry has resolveSync (inline widget), runs it
// locally. Otherwise queues a {id, componentType, config} request for
// POST /api/resolve-widgets (lookup widget).
export function orchestrateBlocks(
  blocks: ReadonlyArray<BlockDto>,
  registry: ReadonlyMap<string, AnyFrontendWidget>,
): OrchestrationResult {
  const result: OrchestrationResult = { inline: [], lookups: [] };
  walk(blocks, registry, result);
  return result;
}

function walk(
  blocks: ReadonlyArray<BlockDto>,
  registry: ReadonlyMap<string, AnyFrontendWidget>,
  out: OrchestrationResult,
): void {
  for (const block of blocks) {
    const widget = registry.get(block.componentType);
    if (widget) {
      if (widget.resolveSync) {
        try {
          const resolved = widget.resolveSync(stripBlockHousekeeping(block));
          out.inline.push({ id: block.id, resolved });
        } catch {
          // Component will render fallback; keep going.
        }
      } else {
        out.lookups.push({
          id: lookupKeyFor(block),
          componentType: block.componentType,
          config: stripBlockHousekeeping(block),
        });
      }
    }
    for (const zone of nestedZonesOf(block)) {
      walk(zone, registry, out);
    }
  }
}
